/**
 * Pure text transforms shared by HtmlWithLatex (admin + student web).
 * Kept free of React so Node can regression-test explanation preservation.
 */

/** ASCII hyphen, unicode minus, or en dash used as a signed-term marker. */
const SIGNED_TERM_MARK = "[\\-\\u2212\\u2013]";
const SIGNED_TERM = `${SIGNED_TERM_MARK}\\d+(?:\\.\\d+)?`;
const SIGNED_LIST_NUMBER = `${SIGNED_TERM_MARK}?\\d+(?:\\.\\d+)?`;
const SIGNED_LIST_GAP = "\u00a0\u00a0\u00a0\u00a0\u00a0";

function hasSignedListTerm(text) {
  return new RegExp(SIGNED_TERM).test(text);
}

/**
 * A line of signed terms and blanks (`2 −4 8 −16 32 ______ ______`), not an equation.
 * The gap after a negative term must stay visible, otherwise `−4 8` reads as `−48`.
 */
function isSignedNumberListLine(line) {
  const t = String(line ?? "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .trim();
  if (!t || !hasSignedListTerm(t)) return false;
  if (/[=+×÷/]|[A-Za-z]/.test(t)) return false;
  // Scores, sports results, and ranges ("3-2, 4-2, 3-3", "1-5, 6-10") are not signed terms.
  if (t.includes(",") || t.includes(";")) {
    const parts = t.split(/[,;]/).map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2 && parts.some((p) => /^\d+(?:\.\d+)?\s*[-–—]\s*\d+(?:\.\d+)?$/.test(p))) {
      return false;
    }
  }
  const spaceTokens = t.split(/\s+/).filter(Boolean);
  if (spaceTokens.length >= 2 && spaceTokens.every((tk) => /^\d+(?:\.\d+)?\s*[-–—]\s*\d+(?:\.\d+)?$/.test(tk))) {
    return false;
  }
  const nums = t.match(new RegExp(SIGNED_LIST_NUMBER, "g")) || [];
  if (nums.length < 3) return false;
  const residue = t.replace(new RegExp(`${SIGNED_LIST_NUMBER}|_{2,}|…+|\\.{2,}|□|[\\s,;]+`, "g"), "");
  return residue.length === 0;
}

/** Keep a readable gap between a negative term and the next number in a list. */
export function spaceSignedNumberListGaps(text) {
  const source = String(text ?? "");
  if (!source || !hasSignedListTerm(source)) return source;
  return source
    .split("\n")
    .map((line) => {
      if (!isSignedNumberListLine(line)) return line;
      let s = line.replace(/&nbsp;/gi, "\u00a0");
      s = s.replace(new RegExp(`(\\d)(${SIGNED_TERM})`, "g"), "$1 $2");
      s = s.replace(
        new RegExp(`(${SIGNED_TERM})([ \\t\\u00a0]*)(?=${SIGNED_TERM_MARK}?\\d)`, "g"),
        (match, num, gap) => {
          const units = String(gap).replace(/\u00a0/g, " ").length;
          if (units >= 4) return match;
          return `${num}${SIGNED_LIST_GAP}`;
        }
      );
      return s;
    })
    .join("\n");
}

/**
 * PDF import drops the power on "expressions for n²" to a baseline "n2".
 * Raise that exponent. Leave ordinary identifiers ("Question n2") alone.
 */
export function liftAlgebraicPlainSquares(html) {
  return String(html ?? "").replace(
    /(\bexpressions?\b[^<]{0,120}?\bfor\s+)([a-z])2\b/gi,
    (_, context, variable) => `${context}${variable}<sup>2</sup>`
  );
}

/** Same gap inside question HTML text, without touching tags. */
export function spaceSignedNumberListsInHtml(html) {
  const s = String(html ?? "");
  if (!s || !hasSignedListTerm(s)) return s;
  if (!/<[a-z]/i.test(s)) return spaceSignedNumberListGaps(s);
  return s.replace(/>([^<]*)</g, (full, text) => {
    if (!hasSignedListTerm(text)) return full;
    return `>${spaceSignedNumberListGaps(text)}<`;
  });
}

/** True when text is a worked explanation — option walkthroughs must stay visible. */
export function looksLikeWorkedExplanation(text) {
  if (!text) return false;
  if (/\b(?:the\s+correct\s+answer\s+is|so\s+the\s+answer\s+is)\b/i.test(text)) return true;
  if (/\bthe\s+other\s+options\b/i.test(text)) return true;
  // Numbered worked steps (1. … 2. …) — common AI explanation shape.
  const numbered = text.match(/(?:^|[\n\r])\s*\d{1,2}[.)]\s+\S/g) || [];
  return numbered.length >= 2;
}

/**
 * Strip MCQ option labels from question stems only.
 * NEVER run on worked explanations — those often analyse A/B/C/D and stripping
 * them drops the closing part of the mark scheme.
 */
export function stripInlineMCQOptions(text) {
  if (!text) return text;
  if (looksLikeWorkedExplanation(text)) return text;
  let working = text;

  working = working.replace(/([|])\s*_{2,}\s*/g, '$1 ');

  const glBoldRe = /\n+(?:\*\*[A-EX-Z]\*\*\s+\S+(?:\s{2,}|\s*$)){3,}\s*$/;
  if (glBoldRe.test(working)) {
    working = working.replace(glBoldRe, '').trim();
  }

  const plainTailRe = /\n+(?:[A-EX-Z]\s+\S{1,15}(?:\s{2,}|\s*$)){3,}\s*$/;
  if (plainTailRe.test(working)) {
    working = working.replace(plainTailRe, '').trim();
  }

  const optStart = working.search(/(?:\s{2,}|[\n\r])A[).]\s+\S/);
  if (optStart >= 10) {
    const afterA = working.substring(optStart);
    const labelCount = (afterA.match(/(?:\s{2,}|[\n\r])[A-E][).]\s+/g) || []).length;
    if (labelCount >= 3) {
      working = working.substring(0, optStart).trim();
    }
  }

  return working.trim();
}

/** Short complete words that must never be treated as PDF mid-word fragments. */
const COMMON_WHOLE_WORDS =
  /^(?:a|an|as|at|be|by|do|for|he|if|in|is|it|me|my|no|of|on|or|so|to|up|us|we|and|the|but|not|are|was|had|has|its|than|then|that|this|with|from|into|also|both|each|more|most|over|such|when|what|who|how|all|can|may|did|does|have|were|been|will|would|could|should|about|after|before|under|until|while|where|which|their|there|these|those|other|every|first|next)$/i;

/**
 * PDF mid-word wraps rejoin; digit→discourse and authored whole-word wraps stay as breaks.
 */
export function convertNewlinesToBr(text) {
  return String(text ?? '')
    .replace(/([a-zA-Z0-9])\n([a-z])/g, (full, beforeChar, afterChar, offset, whole) => {
      const str = String(whole);
      const idx = Number(offset);
      // Start at the lowercase letter (skip beforeChar + newline).
      const rest = str.slice(idx + 2);
      // Digit → new discourse sentence ("12321\nso it is…") — keep the break.
      if (
        /\d/.test(beforeChar) &&
        /^(?:so|then|the|therefore|thus|hence|first|next|finally|after)\b/i.test(rest)
      ) {
        return `${beforeChar}\n${afterChar}`;
      }
      // "5\napples" / "2\nhundreds" — space, not a new step line.
      if (/\d/.test(beforeChar)) {
        return `${beforeChar} ${afterChar}`;
      }
      const wordBefore =
        str
          .slice(0, idx + 1)
          .split(/[\s\n]+/)
          .pop() || '';
      // Mid-word wrap only ("po\nint", "anti\nclockwise") — not "and\nbackwards".
      if (
        wordBefore.length > 0 &&
        wordBefore.length <= 4 &&
        !COMMON_WHOLE_WORDS.test(wordBefore)
      ) {
        return `${beforeChar}${afterChar}`;
      }
      // Authored / whole-word line break — keep \n so it becomes <br>.
      return `${beforeChar}\n${afterChar}`;
    })
    .replace(/\n/g, '<br>');
}

function visibleCodeText(innerHtml) {
  return String(innerHtml ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&#92;|&#x5c;/gi, '\\')
    .replace(/&#36;|&#x24;/gi, '$')
    .replace(/\s+/g, ' ')
    .trim();
}

function textIsLatexMathSource(text) {
  const t = String(text ?? '').trim();
  if (!t || t.length > 4000) return false;
  if (/\\(?:frac|dfrac|tfrac|sqrt|sum|int|binom|times|div|cdot|left|right|begin|text|mathbf)\b/.test(t)) {
    return true;
  }
  if (/\\\([\s\S]+?\\\)|\\\[[\s\S]+?\\\]/.test(t)) return true;
  const spans = t.match(/\$[^$\n]+\$/g) || [];
  return spans.some((span) => /\\[a-zA-Z]|\^/.test(span));
}

/**
 * MathJax skips code/pre, so a fraction stored in a code span shows as `$\\frac…$`.
 * Unwrap when the contents are maths. Leave real snippets (`print(x)`) wrapped.
 */
export function unwrapLatexDisplayedAsCode(html) {
  let s = String(html ?? '');
  if (!s) return s;
  if (s.includes('`')) {
    s = s.replace(/```(?:[a-zA-Z]*)\s*([\s\S]*?)```/g, (full, inner) =>
      textIsLatexMathSource(visibleCodeText(inner)) ? String(inner).trim() : full
    );
    s = s.replace(/`([^`]+)`/g, (full, inner) =>
      textIsLatexMathSource(visibleCodeText(inner)) ? inner : full
    );
  }
  if (!/<\s*(?:code|pre|kbd|samp)\b/i.test(s)) return s;
  for (let i = 0; i < 4; i += 1) {
    const next = s.replace(
      /<(code|pre|kbd|samp)\b[^>]*>([\s\S]*?)<\/\1>/gi,
      (full, _tag, inner) => (textIsLatexMathSource(visibleCodeText(inner)) ? inner : full)
    );
    if (next === s) break;
    s = next;
  }
  return s;
}
